import {FormikProps} from "formik";
import {Button, Checkbox, Form, Input, message, Modal, Select} from "antd";
import React, {FC, memo, useEffect} from "react";
import moment from "moment";
import {blue, red} from "@ant-design/colors";
import {SaveOutlined} from "@ant-design/icons";
import {
    Booking,
    GetBookingByIdDocument,
    GetPaidScheduledPaymentsDocument,
    GetUnpaidScheduledPaymentsDocument,
    ModifyScheduledPaymentInput,
    ScheduledPaymentType,
    useGetActiveDunningQuery,
    useGetCompanyInfoQuery,
    useModifyScheduledPaymentMutation,
} from "../../generated/graphql";
import i18n from "../../services/i18n";
import LayoutForm from "../../views/LayoutForm/LayoutForm";
import {createInputHelpers} from "../../helpers/createInputHelpers";
import {isDefined} from "../../helpers/typeScriptHelpers";
import {erasedRecordMessage} from "../../helpers/erasedRecordMessage";
import {resolveServerErrorMessage} from "../../helpers/resolveServerErrorMessage";
import MoneyInput from "../../views/MoneyInput/MoneyInput";
import {AUTO_COURSE_FEE_TITLE, COURSE_FEE_TITLE_HINT, normalizeCourseFeeTitle, options} from "../../views/PanelCardPayments/AddNamedScheduledPaymentForm";
import DateInput from "../../views/DateInput/DateInput";

type EditScheduledPaymentProps = {
    formikProps: FormikProps<ModifyScheduledPaymentInput>;
    setEditPaymentOpen: (value: boolean) => void;
    bookingId: Booking["id"];
    refetch?: () => void;
    // Mollie-Lock (Plan 2026-05-20): true, wenn die Rate ueber Mollie bereits
    // bezahlt ist — sperrt Betrag und Zahldatum/Unbezahlt im Form.
    isMolliePaid?: boolean;
    // Rate ist schon abgerechnet: die gespeicherte Rechnungs-PDF ändert sich beim Umbenennen nicht.
    hasInvoice?: boolean;
};

const INVOICED_TITLE_HINT = "Die bereits erstellte Rechnung bleibt unverändert. Den neuen Text zeigt erst eine Korrekturrechnung.";

const EditScheduledPaymentForm: FC<EditScheduledPaymentProps> = ({
    formikProps,
    setEditPaymentOpen,
    bookingId,
    refetch,
    isMolliePaid = false,
    hasInvoice = false,
}) => {
    const [unpaid, setUnpaid] = React.useState(false);

    const handleSetUnpaid = () => {
        formikProps.setFieldValue("paymentDone", null);
        setUnpaid(true);
        setTimeout(() => {
            setUnpaid(false);
        }, 300);
    };

    const {
        help,
        validateStatus,
        handleDatePickerChange,
        handleDatePickerBlur,
        handleBlur,
        handleNumberInputChange,
        handleCheckboxChange,
        handleTextInputChange,
    } = createInputHelpers(formikProps);

    const {values, errors} = formikProps;
    const titleDisabled = values.type !== ScheduledPaymentType.Custom && values.type !== ScheduledPaymentType.Standard;

    useEffect(() => {
        if (values.paymentDone === undefined) {
            formikProps.setFieldValue("paymentDone", null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.paymentDone]);

    const refetchQueries = [
        "GetUnpaidScheduledPayments",
        {query: GetPaidScheduledPaymentsDocument},
        "GetUnpaidScheduledPayments",
        {
            query: GetUnpaidScheduledPaymentsDocument,
            // variables: {options: {}},
        },
        "GetBookingById",
        {
            query: GetBookingByIdDocument,
            variables: {id: bookingId},
        },
    ];

    // Nicht auf die Refetches warten: Scheitert nur das Nachladen, würde die Mutation sonst als
    // fehlgeschlagen gemeldet, obwohl längst gespeichert ist (#1128).
    const [modifyScheduledPayment] = useModifyScheduledPaymentMutation({refetchQueries});
    // #1517: null ohne Flag oder ohne aktive Mahnung
    const {data: companyInfo, loading: companyInfoLoading} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const {data: dunningData, loading: dunningLoading} = useGetActiveDunningQuery({variables: {bookingId}, skip: !bookingId || !companyInfo?.companyInfo?.subscription?.featureDunning, fetchPolicy: "network-only"});

    const handleSubmit = async (values: ModifyScheduledPaymentInput) => {
        // Sperrt den Speichern-Button, solange die Mutation läuft: ohne das schickte ein zweiter Klick
        // oder Enter die Mutation erneut (zwei identische ModifyScheduledPayment-Requests, 07.10.2026).
        formikProps.setSubmitting(true);
        try {
            const res = await modifyScheduledPayment({
                variables: {
                    modifyScheduledPaymentInput: {
                        id: values.id,
                        paymentAmount: values.paymentAmount,
                        paymentDue: values.paymentDue,
                        paymentDone: values.paymentDone,
                        paymentMethodId: values.paymentMethodId,
                        sepaClearance: values.sepaClearance,
                        note: values.note,
                        title: normalizeCourseFeeTitle(values.type, values.title),
                        type: values.type,
                    },
                },
            });

            refetch?.();
            if (res.data?.modifyScheduledPayment) {
                formikProps.resetForm();
                setEditPaymentOpen(false);
            }
        } catch (error) {
            // Der Server sagt, warum er ablehnt (z. B. Sperre nach DSGVO-Löschantrag); ohne Meldung blieb der Dialog stumm offen.
            message.error(erasedRecordMessage(error) ?? resolveServerErrorMessage(error, "Die Änderung konnte nicht gespeichert werden."));
        } finally {
            formikProps.setSubmitting(false);
        }
    };

    /** Betrag oder Fälligkeit einer gemahnten Buchung ändern: Rückfrage wie in der Skizze, blockiert nicht */
    const confirmIfDunned = (values: ModifyScheduledPaymentInput) => {
        const dunning = dunningData?.activeDunning;
        const initial = formikProps.initialValues;
        const changesAmount = Number(values.paymentAmount) !== Number(initial.paymentAmount);
        const changesDue = !moment(values.paymentDue).isSame(initial.paymentDue, "day");

        if (!dunning || (!changesAmount && !changesDue)) {
            void handleSubmit(values);
            return;
        }
        const trDunning = i18n.containers.invoice.BookingDunnings;
        const level = trDunning.levels[dunning.level]();
        const date = moment(dunning.createDate).format("DD.MM.YYYY");

        Modal.confirm({
            title: trDunning.rate.editTitle(),
            // Nur die Frist verschoben: Der Betrag der Mahnung stimmt weiter, also der Text ohne „weicht ab“
            content: changesAmount ? trDunning.rate.text({level, date}) : trDunning.rate.deleteText({level, date}),
            okText: trDunning.rate.ok(),
            cancelText: trDunning.rate.cancel(),
            onOk: () => handleSubmit(values),
        });
    };

    return (
        <LayoutForm
            columns={5}
            onFinish={() => confirmIfDunned(values)}
            name="BookingForm"
        >
            <Form.Item
                label="Typ"
                validateStatus={validateStatus("type")}
                help={help("type")}
                style={{gridColumn: "span 2"}}
            >
                <Select
                    data-testid="type"
                    onChange={(value: ScheduledPaymentType) => {
                        formikProps.setFieldValue("type", value);
                        const selectedOption = options.find(
                            (option) => option.type === value,
                        );
                        if (value === ScheduledPaymentType.Standard) {
                            formikProps.setFieldValue("title", "");
                        } else if (value === ScheduledPaymentType.Custom) {
                            formikProps.setFieldValue("title", "");
                        } else if (selectedOption) {
                            formikProps.setFieldValue("title", selectedOption.label);
                        }
                    }}
                    value={values.type}
                    style={{width: "100%"}}
                    size="small"
                >
                    {options.map((option) => (
                        <Select.Option
                            key={option.type}
                            value={option.type}
                        >
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                label="Bezeichnung auf der Rechnung"
                validateStatus={validateStatus("title")}
                help={help("title")}
                style={{gridColumn: "span 3"}}
                extra={[values.type === ScheduledPaymentType.Standard ? COURSE_FEE_TITLE_HINT : "", hasInvoice && !titleDisabled ? INVOICED_TITLE_HINT : ""].filter(Boolean).join(" ") || undefined}
            >
                <Input
                    data-testid="title"
                    onChange={handleTextInputChange("title")}
                    onBlur={handleBlur("title")}
                    value={values.type === ScheduledPaymentType.Standard && !values.title ? AUTO_COURSE_FEE_TITLE : values.title ?? ""}
                    name="title"
                    disabled={titleDisabled}
                    size="small"
                />
            </Form.Item>
            <Form.Item
                label={i18n.views.TableColumnTitles.paymentDue()}
                validateStatus={validateStatus("paymentDue")}
                help={help("paymentDue")}
                required
            >
                <DateInput
                    data-testid="paymentDue"
                    onChange={handleDatePickerChange("paymentDue")}
                    value={
                        isDefined(values.paymentDue)
                            ? moment(values.paymentDue)
                            : undefined
                    }
                    displayFormat="DD.MM.YYYY"
                    onOpenChange={handleDatePickerBlur("paymentDue")}
                    status={
                        values.paymentDue && errors.paymentDue
                            ? "error"
                            : undefined
                    }
                    style={{width: "100%"}}
                    size="small"
                />
            </Form.Item>
            <Form.Item
                label={i18n.views.TableColumnTitles.paymentAmount()}
                validateStatus={validateStatus("paymentAmount")}
                help={help("paymentAmount")}
                required
                // style={{gridColumn: "span 2"}}
                // tooltip={`This is a required field ${values.paymentAmount}}`}
            >
                <MoneyInput
                    data-testid="paymentAmount"
                    disabled={isMolliePaid}
                    onChange={handleNumberInputChange("paymentAmount")}
                    value={
                        isDefined(values.paymentAmount)
                            ? values.paymentAmount
                            : undefined
                    }
                    onBlur={handleBlur("paymentAmount")}
                    size="small"
                />
                {isMolliePaid && (
                    <div style={{fontSize: 12, color: "#979ea5"}}>
                        {i18n.containers.payments.PaymentsListColumns.mollieAmountLockedHelp()}
                    </div>
                )}
            </Form.Item>
            <Form.Item
                label={i18n.views.TableColumnTitles.paymentDone()}
                validateStatus={validateStatus("paymentDone")}
                help={help("paymentDone")}
                tooltip={
                    values.paymentDone === null
                        ? `Um diese Zahlung als bezahlt zu markieren, muss ein Datum ausgewählt werden.`
                        : undefined
                }
            >
                <DateInput
                    data-testid="paymentDone"
                    onChange={handleDatePickerChange("paymentDone")}
                    value={
                        isDefined(values.paymentDone)
                            ? moment(values.paymentDone)
                            : undefined
                    }
                    displayFormat="DD.MM.YYYY"
                    onOpenChange={handleDatePickerBlur("paymentDone")}
                    status={
                        values.paymentDone && errors.paymentDone
                            ? "error"
                            : undefined
                    }
                    style={{width: "100%"}}
                    defaultPickerValue={moment()}
                    open={unpaid ? false : undefined}
                    placeholder="Unbezahlt"
                    renderExtraFooter={() => {
                        // Mollie-Lock (Plan 2026-05-20): "Als unbezahlt markieren"
                        // entfaellt fuer ueber Mollie bezahlte Raten (Status-Drift).
                        if (!isMolliePaid && values.paymentDone !== null) {
                            return (
                                <>
                                    <Button
                                        block
                                        size="small"
                                        style={{color: red[5]}}
                                        onClick={() => {
                                            handleSetUnpaid();
                                        }}
                                    >
                                        {"Als unbezahlt markieren"}
                                    </Button>
                                </>
                            );
                        }
                        return null;
                    }}
                    showNow
                    size="small"
                    disabled={!values.paymentDone || isMolliePaid}
                />
            </Form.Item>
            <Form.Item
                data-testid="sepaClearance"
                label={" "}
                validateStatus={validateStatus("sepaClearance")}
                help={help("sepaClearance")}
            >
                <Checkbox
                    onChange={handleCheckboxChange("sepaClearance")}
                    checked={Boolean(values.sepaClearance)}
                >
                    {i18n.views.TableColumnTitles.sepaClearance()}
                </Checkbox>
            </Form.Item>
            <Form.Item
                data-testid="submitButton"
                label={" "}
                // style={{width: "100%"}}
                style={{alignSelf: "flex-end"}}
            >
                <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    // Ohne geladenen Mahnstand fehlte sonst die Rückfrage
                    disabled={formikProps.isSubmitting || companyInfoLoading || dunningLoading}
                    block
                >
                    {"Speichern"}
                </Button>
            </Form.Item>
            <Form.Item
                label="Notiz zum Posten"
                validateStatus={validateStatus("note")}
                help={help("note")}
                style={{gridColumn: "span 5", marginTop: -15}}
            >
                <Input.TextArea
                    rows={4}
                    value={values.note ?? ""}
                    onChange={(e) =>
                        formikProps.setFieldValue("note", e.target.value)
                    }
                />
            </Form.Item>
        </LayoutForm>
    );
};

export default memo(EditScheduledPaymentForm);
